import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { createTheme } from "@mui/material";

const useTheme = () => {
  const { themeMode } = useSelector((state) => state.mainReducer);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeMode,
          primary: {
            light: "#1BBFB2",
            main: "#0D9A8F",
            dark: "#0A7A72",
            contrastText: "#ffffff",
          },
          secondary: {
            main: "#546e7a",
            contrastText: "#ffffff",
          },
        },
        shape: { borderRadius: 10 },
        components: {
          // ── Inputs ────────────────────────────────────────────────
          MuiButton: { defaultProps: { size: "small" } },
          MuiTextField: { defaultProps: { size: "small" } },
          MuiOutlinedInput: { defaultProps: { size: "small" } },
          MuiSelect: { defaultProps: { size: "small" } },
          MuiAutocomplete: { defaultProps: { size: "small" } },

          // ── Paper ─────────────────────────────────────────────────
          // Applied to every Paper in the app — form wrappers, table wrappers, etc.
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none", // prevents dark-mode gradient artifact
                borderRadius: 10,
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
              },
              // Lift + teal glow on all elevation-3 card containers
              elevation3: {
                "&:hover": {
                  boxShadow:
                    "0 8px 28px rgba(13,154,143,0.14), 0 2px 8px rgba(0,0,0,0.08)",
                  transform: "translateY(-1px)",
                },
              },
            },
          },

          // ── Card (used by agent charts, KPI cards, etc.) ──────────
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 10,
                backgroundImage: "none",
                transition: "box-shadow 0.2s ease, transform 0.2s ease",
                "&:hover": {
                  boxShadow:
                    "0 8px 28px rgba(13,154,143,0.14), 0 2px 8px rgba(0,0,0,0.08)",
                  transform: "translateY(-1px)",
                },
              },
            },
          },

          // ── Table Container ───────────────────────────────────────
          MuiTableContainer: {
            styleOverrides: {
              root: ({ theme }) => ({
                borderRadius: 10,
                border: `1px solid ${theme.palette.divider}`,
                overflowX: "auto",  // allow horizontal scroll on wide tables
                overflowY: "visible",
              }),
            },
          },

          // ── Table Cells ───────────────────────────────────────────
          MuiTableCell: {
            styleOverrides: {
              root: {
                padding: "8px 14px",
                fontSize: "0.8125rem",
              },
              head: ({ theme }) => ({
                padding: "10px 14px",
                fontWeight: 600,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                whiteSpace: "nowrap",
                // Checkboxes inside header
                "& .MuiCheckbox-root": {
                  color: theme.palette.primary.contrastText,
                  padding: 0,
                },
                // Sort labels
                "& .MuiTableSortLabel-root": {
                  color: `${theme.palette.primary.contrastText} !important`,
                  "&:hover": {
                    color: `${theme.palette.primary.contrastText} !important`,
                    opacity: 0.85,
                  },
                  "&.Mui-active": {
                    color: `${theme.palette.primary.contrastText} !important`,
                  },
                  "& .MuiTableSortLabel-icon": {
                    color: `${theme.palette.primary.contrastText} !important`,
                    opacity: 0.8,
                  },
                },
              }),
            },
          },

          // ── Table Rows ────────────────────────────────────────────
          MuiTableRow: {
            styleOverrides: {
              root: ({ theme }) => ({
                height: "auto",
                transition: "background-color 0.15s ease",
                // Zebra stripe (body rows — header cell bg overrides this visually)
                "&:nth-of-type(even)": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.025)"
                      : "rgba(0,0,0,0.018)",
                },
                // Row hover with teal tint
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(13,154,143,0.08) !important"
                      : "rgba(13,154,143,0.055) !important",
                },
                // Remove bottom border on last row
                "&:last-child td, &:last-child th": { border: 0 },
              }),
            },
          },
          // ── DataGrid ──────────────────────────────────────────────
          MuiDataGrid: {
            styleOverrides: {
              columnHeaders: {
                backgroundColor: "#0D9A8F",
                color: "#fff",
              },
              columnHeaderTitle: {
                fontWeight: 600,
                fontSize: "0.8125rem",
              },
              columnHeader: {
                "&:focus, &:focus-within": { outline: "none" },
                // checkbox in header
                "& .MuiCheckbox-root": { color: "#fff" },
                "& .MuiDataGrid-sortIcon": { color: "#fff" },
                "& .MuiDataGrid-menuIconButton": { color: "#fff" },
                "& .MuiDataGrid-columnSeparator": { color: "rgba(255,255,255,.3)" },
              },
            },
          },
        },
        typography: { fontSize: 13 },
      }),
    [themeMode]
  );

  useEffect(() => {
    const $style = document.createElement("style");
    if (theme) {
      document.head.appendChild($style);
      $style.textContent = `
        :root {
          --colorPrimary: ${theme.palette.primary.main};
          --contrastText: ${theme.palette.primary.contrastText};
        }`;
    }
  }, [theme]);

  return theme;
};

export default useTheme;
